import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, Star, ArrowRight, Check, Package } from "lucide-react";
import { gtmFormSubmit, gtmClick } from "@/lib/gtm";
import { SEOHead, breadcrumbSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGET_OPTIONS = ["$200 – $500", "$500 – $1,000", "$1,000+", "Not sure yet"];
const INDUSTRY_OPTIONS = ["Tradie / Trade Services", "Hospitality / Cafe / Restaurant", "eCommerce / Online Store", "Health & Fitness", "Real Estate", "Financial / Accounting", "Tech / SaaS", "Beauty / Hair / Salon", "Professional Services", "Construction / Building", "Automotive", "Other"];
const TIMELINE_OPTIONS = ["ASAP — I need it yesterday", "Within 1–2 weeks", "Within a month", "No rush, just exploring"];
const STYLE_OPTIONS = ["Clean & modern", "Bold & colourful", "Minimal & simple", "Professional & corporate", "Not sure — surprise me"];

const REVIEWS = [
  { name: "Sarah Mitchell", business: "Mitchell's Cafe, Richmond", text: "Couldn't believe the quality for $499. Live in 36 hours and I've had 3 new bookings through it already.", rating: 5 },
  { name: "Dave Kowalski", business: "Kowalski Plumbing, Dandenong", text: "This paid for itself in the first week. Calls are up, Google ranking is up. Should've done it years ago!", rating: 5 },
  { name: "Priya Sharma", business: "Priya's Yoga Studio, St Kilda", text: "The booking system alone saves me hours every week. Professional, fast, and the team actually cared.", rating: 5 },
];

function RadioGroup({ label, options, value, onChange, testId }) {
  return (
    <div>
      <p className="text-base font-black mb-3 text-black" style={{ fontFamily: "Outfit, sans-serif" }}>{label}</p>
      <div className="grid grid-cols-2 gap-2">
        {options.map(opt => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`text-left text-sm font-semibold px-4 py-3 rounded-xl border-2 transition-all ${
              value === opt
                ? "bg-[#FFD600] border-black"
                : "bg-[#FFFBE6] border-[#E5D660] hover:border-black hover:bg-[#FFF3B0]"
            }`}
            style={value === opt ? { boxShadow: "2px 2px 0px 0px rgba(0,0,0,1)" } : {}}
            data-testid={`${testId}-${opt.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
          >
            {value === opt && <Check size={12} className="inline mr-1.5" strokeWidth={3} />}
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function QuotePage() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", business_name: "",
    budget: "", industry: "", timeline: "", needs_call: "",
    has_logo: "", sells_products: "", has_existing_site: "", preferred_style: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please enter your name and email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        source: "quote",
        package_interest: form.budget,
      });
      gtmFormSubmit("quote_form", { email: form.email, budget: form.budget, industry: form.industry, timeline: form.timeline });
      setSubmitted(true);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <main className="pt-16 min-h-screen bg-[#F4F4F5]" data-testid="quote-success">
        <div className="bg-[#FFD600] border-b-4 border-black py-16 text-center">
          <div className="w-20 h-20 bg-green-500 border-4 border-black rounded-full flex items-center justify-center mx-auto mb-6" style={{ boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)" }}>
            <Check size={40} className="text-white" strokeWidth={3} />
          </div>
          <h1 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>Quote Request Sent!</h1>
          <p className="text-lg font-semibold mt-3 max-w-lg mx-auto px-4">Someone from our team will be in touch shortly to discuss your project.</p>
        </div>
        <div className="container-main max-w-2xl py-12 px-4">
          <div className="bg-white border-2 border-black rounded-2xl p-8 text-center mb-8" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
            <p className="text-lg font-bold mb-4">Need help sooner?</p>
            <a href="tel:0466476264" className="inline-flex items-center gap-2 bg-black text-white font-black px-8 py-4 rounded-xl border-2 border-black text-lg hover:-translate-y-0.5 transition-transform" style={{ boxShadow: "3px 3px 0px 0px #FFD600" }} data-testid="quote-call-btn">
              <Phone size={20} /> Call 0466 476 264
            </a>
          </div>
          <div className="text-center">
            <p className="font-bold text-gray-600 mb-4">Or if you already know what you need:</p>
            <Link to="/purchase" className="btn-primary inline-flex items-center gap-2 text-lg" data-testid="quote-choose-package-btn">
              <Package size={18} /> Choose a Package
            </Link>
          </div>
        </div>

        {/* Testimonials */}
        <div className="bg-white border-t-4 border-black py-16">
          <div className="container-main max-w-5xl px-4">
            <h2 className="text-3xl font-black text-center mb-10" style={{ fontFamily: "Outfit, sans-serif" }}>What Our Customers Say</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {REVIEWS.map((r, i) => (
                <div key={i} className="bg-[#F4F4F5] border-2 border-black rounded-2xl p-6" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
                  <div className="flex mb-3">{[...Array(r.rating)].map((_, j) => <Star key={j} size={14} className="text-[#FFD600] fill-[#FFD600]" />)}</div>
                  <p className="text-sm italic text-gray-700 mb-4">"{r.text}"</p>
                  <p className="font-black text-sm">{r.name}</p>
                  <p className="text-xs text-gray-500">{r.business}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-16 min-h-screen bg-[#F4F4F5]" data-testid="quote-page">
      <SEOHead
        title="Get a Free Quote"
        description="Get a free, no-obligation quote for your new website. Tell us about your business and we'll recommend the perfect package. Professional websites from $499."
        path="/quote"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Free Quote", path: "/quote" }])}
      />
      <Breadcrumbs items={[{ name: "Get a Free Quote", path: "/quote" }]} />
      <div className="bg-[#FFD600] border-b-4 border-black py-12 text-center">
        <h1 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>Get a Free Quote</h1>
        <p className="font-semibold mt-2 max-w-lg mx-auto px-4">Tell us about your project and we'll get back to you with a tailored quote — no obligations.</p>
      </div>

      <div className="container-main max-w-4xl py-12 px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6" data-testid="quote-form">
            {/* Contact Details */}
            <div className="bg-white border-2 border-black rounded-2xl overflow-hidden" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
              <div className="bg-[#FFD600] px-8 py-4 border-b-2 border-black">
                <h2 className="text-xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>Your Details</h2>
              </div>
              <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Full Name *</label>
                  <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Jane Smith"
                    className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]" data-testid="quote-name" />
                </div>
                <div>
                  <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="jane@business.com.au"
                    className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]" data-testid="quote-email" />
                </div>
                <div>
                  <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Phone</label>
                  <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="0400 000 000"
                    className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]" data-testid="quote-phone" />
                </div>
                <div>
                  <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Business Name</label>
                  <input value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} placeholder="Jane's Cafe"
                    className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]" data-testid="quote-business" />
                </div>
              </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="bg-white border-2 border-black rounded-2xl overflow-hidden" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
              <div className="bg-[#FFD600] px-8 py-4 border-b-2 border-black">
                <h2 className="text-xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>About Your Project</h2>
              </div>
              <div className="p-8 space-y-6">
              <RadioGroup label="What's your budget?" options={BUDGET_OPTIONS} value={form.budget} onChange={v => setForm({...form, budget: v})} testId="quote-budget" />
              <RadioGroup label="Industry / Business Type" options={INDUSTRY_OPTIONS} value={form.industry} onChange={v => setForm({...form, industry: v})} testId="quote-industry" />
              <RadioGroup label="How quickly do you need the website?" options={TIMELINE_OPTIONS} value={form.timeline} onChange={v => setForm({...form, timeline: v})} testId="quote-timeline" />
              <RadioGroup label="What style do you prefer?" options={STYLE_OPTIONS} value={form.preferred_style} onChange={v => setForm({...form, preferred_style: v})} testId="quote-style" />
              </div>
            </div>

            {/* Quick Questions */}
            <div className="bg-white border-2 border-black rounded-2xl overflow-hidden" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
              <div className="bg-black px-8 py-4 border-b-2 border-black">
                <h2 className="text-xl font-black text-[#FFD600]" style={{ fontFamily: "Outfit, sans-serif" }}>A Few Quick Questions</h2>
              </div>
              <div className="p-8 space-y-6">
              <RadioGroup label="Do you need a 1:1 call to figure out what you need?" options={["Yes please", "No, I know what I want", "Maybe — depends on the quote"]} value={form.needs_call} onChange={v => setForm({...form, needs_call: v})} testId="quote-call" />
              <RadioGroup label="Do you have a logo?" options={["Yes", "No — I need one made", "I have one but want a new one"]} value={form.has_logo} onChange={v => setForm({...form, has_logo: v})} testId="quote-logo" />
              <RadioGroup label="Will you sell products through the website?" options={["Yes", "No", "Maybe in the future"]} value={form.sells_products} onChange={v => setForm({...form, sells_products: v})} testId="quote-products" />
              <RadioGroup label="Do you have an existing website?" options={["Yes — needs a redesign", "Yes — but starting fresh", "No — this is brand new"]} value={form.has_existing_site} onChange={v => setForm({...form, has_existing_site: v})} testId="quote-existing" />
              </div>
            </div>

            {/* Additional Notes */}
            <div className="bg-white border-2 border-black rounded-2xl overflow-hidden" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
              <div className="p-8">
                <p className="text-base font-black mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>Anything else you'd like us to know?</p>
                <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={4} placeholder="Tell us more about your business, what pages you need, any inspiration sites you like..."
                  className="w-full border-2 border-[#E5D660] bg-[#FFFBE6] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600] focus:border-black resize-none" data-testid="quote-message" />
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-[#FFD600] border-2 border-black font-black text-xl py-5 rounded-2xl flex items-center justify-center gap-3 transition-all hover:-translate-y-1 disabled:opacity-60"
              style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}
              data-testid="submit-quote-btn">
              <Send size={20} /> {loading ? "Sending..." : "Send My Quote Request"}
            </button>
          </form>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-black text-white border-2 border-black rounded-2xl p-6" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
              <h3 className="font-black text-lg mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>Why Get a Quote?</h3>
              <ul className="space-y-3 text-sm">
                {["100% free, no obligations", "Tailored to your business", "Response within 24 hours", "Expert advice included", "No pushy sales — promise"].map((t, i) => (
                  <li key={i} className="flex items-center gap-2"><Check size={14} className="text-[#FFD600] shrink-0" strokeWidth={3} />{t}</li>
                ))}
              </ul>
            </div>

            <div className="bg-[#FFD600] border-2 border-black rounded-2xl p-6" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
              <h3 className="font-black mb-2">Already know what you need?</h3>
              <p className="text-sm mb-4">Skip the quote and get started right away from $499.</p>
              <Link to="/purchase" className="btn-primary inline-flex items-center gap-2 w-full justify-center" data-testid="quote-sidebar-packages">
                <Package size={16} /> Choose a Package
              </Link>
            </div>

            <div className="bg-white border-2 border-black rounded-2xl p-6 text-center" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
              <p className="font-black mb-2">Prefer to chat?</p>
              <a href="tel:0466476264" className="inline-flex items-center gap-2 font-black text-lg text-[#0055FF]" data-testid="quote-sidebar-phone">
                <Phone size={18} /> 0466 476 264
              </a>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mt-16">
          <h2 className="text-3xl font-black text-center mb-10" style={{ fontFamily: "Outfit, sans-serif" }}>What Our Customers Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {REVIEWS.map((r, i) => (
              <div key={i} className="bg-white border-2 border-black rounded-2xl p-6" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
                <div className="flex mb-3">{[...Array(r.rating)].map((_, j) => <Star key={j} size={14} className="text-[#FFD600] fill-[#FFD600]" />)}</div>
                <p className="text-sm italic text-gray-700 mb-4">"{r.text}"</p>
                <p className="font-black text-sm">{r.name}</p>
                <p className="text-xs text-gray-500">{r.business}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
